import React, { useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState, useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { CustomTransComponent, Hierarchy, renderGroupHierarchyIcons } from '../../components';
import {
    useCreateGroupMutation,
    useDeleteGroupMutation,
    useGetAllGroupsMutation,
    useMoveExistingGroupsAndDevicesMutation,
} from '@fiji/common/src/features/group/groupApi';
import { useTheme } from '@mui/material/styles';
import { EmptyState } from '@brightlayer-ui/react-components';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { Box, Button, Stack, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    addGroupModal,
    selectRefetchGroupHierarchy,
    setRefetchGroupHierarchy,
} from '@fiji/common/src/features/group/groupSlice';
import { GroupHierarchySkeleton } from './GroupHierarchySkeleton';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useNewCustomTranslations, useRBAC } from '../../hooks';
import * as Colors from '@brightlayer-ui/colors';
import { useSwitchOrgMutation } from '@fiji/common/src/features/orgManagement/orgApi';
import { useDeleteDeviceMutation } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { DEFAULT_HIERARCHY_SIZE } from '@fiji/common';
import { Device } from '@brightlayer-ui/icons-mui';
import { setAppHeaderTitle, setDrawerHeaderTranslationKey } from '@fiji/common/src/features/common/commonSlice';
import { useTranslation } from 'react-i18next';
import { HIERARCHICAL_SORT_ORDER } from '@fiji/common/src/constants';

const buildEmptyStateButtonSx = (theme: any): any => {
    const primary = theme?.palette?.primary;
    return {
        border: `1px solid ${primary?.main}`,
        '&:hover': {
            backgroundColor: primary?.[50],
        },
    };
};

const shouldSkipFetchHierarchy = (
    forceRefresh: boolean,
    paginationPayload: any,
    hierarchyMappingData: any,
    nodeId: any
): boolean => !forceRefresh && !paginationPayload?.page && Boolean(hierarchyMappingData?.[nodeId]);

const resolvePrevMappedRecords = (prev: any, nodeId: any, paginationPayload: any, forceRefresh: boolean): any[] => {
    if (!paginationPayload?.page || forceRefresh) return [];
    return prev?.[nodeId]?.records ?? [];
};

const buildGroupsDataUpdate = (prev: any, paginationPayload: any, childHierarchyData: any): any => {
    const childData = childHierarchyData?.data;
    const isPagination = Boolean(paginationPayload?.page);
    const prevRecords = isPagination ? prev?.records ?? [] : [];
    const childRecords = childData?.records ?? [];
    return {
        records: [...prevRecords, ...childRecords],
        total: childData?.total,
    };
};

const buildHierarchyMappingUpdate = (
    prev: any,
    currentNode: any,
    paginationPayload: any,
    childHierarchyData: any,
    forceRefresh: boolean
): any => {
    const nodeId = currentNode?.id;
    const childData = childHierarchyData?.data;
    const prevRecords = resolvePrevMappedRecords(prev, nodeId, paginationPayload, forceRefresh);
    const childRecords = childData?.records ?? [];
    return {
        ...prev,
        [nodeId]: {
            records: [...prevRecords, ...childRecords],
            total: childData?.total,
        },
    };
};

export const GroupHierarchy = ({ handleHideDrawer }: { handleHideDrawer: any }): JSX.Element => {
    const theme: any = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const selectedOrg = useTypedSelector((rootstate) => rootstate.org.selectedOrg);
    const currentRealmName = useTypedSelector((rootstate) => rootstate.common.selectedRealm);
    const hierarchyRefresh = useTypedSelector(selectRefetchGroupHierarchy);
    const sortOption = useTypedSelector((rootstate) => rootstate.common.sortOption);
    const [getFirstLevelGroup, { data }]: any = useGetAllGroupsMutation();
    const [getGroupChildren]: any = useGetAllGroupsMutation();
    const { t } = useTranslation();
    const { groupId } = useParams();
    const [sortOrder, setSortOrder] = React.useState<string>(sortOption === HIERARCHICAL_SORT_ORDER ? '' : sortOption);
    useNewCustomTranslations('GROUP_MANAGEMENT');
    useNewCustomTranslations('COMMON');

    const [, { isSuccess: createSuccess }] = useCreateGroupMutation({
        fixedCacheKey: 'createGroup',
    });
    const [, { isSuccess: deletionSuccess }] = useDeleteGroupMutation({
        fixedCacheKey: 'deleteGroup',
    });
    const [, { isSuccess: deviceDeletionSuccess }] = useDeleteDeviceMutation({
        fixedCacheKey: 'deleteDevice',
    });
    const [, { isSuccess: moveSuccess }] = useMoveExistingGroupsAndDevicesMutation({
        fixedCacheKey: 'moveData',
    });
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canCreateGroup = hasPermission('group-management_create-groups');
    const canCreateDevice = hasPermission('create-devices');
    const selectedNode = useTypedSelector((rootstate: RootState) => rootstate?.common?.selectedNode);
    const [, { isSuccess: switchOrgSuccess }] = useSwitchOrgMutation({ fixedCacheKey: 'switchOrg' });
    const [groupsData, setGroupsData] = useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = useState<any>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = useState([]);
    const [controlledExpandedNodes, setControlledExpandedNodes] = useState<string[]>([]);

    const resetHandler = (): void => {
        if (sortOrder !== HIERARCHICAL_SORT_ORDER) {
            getFirstLevelGroup({
                body: { page: 0, size: DEFAULT_HIERARCHY_SIZE, sort: { key: 'name', sortType: sortOrder } },
            });
        } else {
            getFirstLevelGroup({ body: { page: 0, size: DEFAULT_HIERARCHY_SIZE } });
        }
        setGroupsData(null);
        setHierarchyMappingData({});
        setControlledExpandedNodes([]);
    };

    React.useEffect(() => {
        if (hierarchyRefresh?.refetch) {
            dispatch(setRefetchGroupHierarchy({}));
            resetHandler();
        }
    }, [hierarchyRefresh?.refetch]);

    React.useEffect(() => {
        if (createSuccess || deletionSuccess || moveSuccess || deviceDeletionSuccess) {
            resetHandler();
        }
    }, [createSuccess, deletionSuccess, moveSuccess, deviceDeletionSuccess]);

    React.useEffect(() => {
        if (!groupsData?.length) {
            setGroupsData(data?.data);
        }
    }, [data]);

    React.useEffect(() => {
        if (selectedOrg?.id) {
            if (sortOrder !== HIERARCHICAL_SORT_ORDER) {
                getFirstLevelGroup({
                    body: { page: 0, size: DEFAULT_HIERARCHY_SIZE, sort: { key: 'name', sortType: sortOrder } },
                });
            } else {
                getFirstLevelGroup({ body: { page: 0, size: DEFAULT_HIERARCHY_SIZE } });
            }
        }
        if (switchOrgSuccess) {
            setGroupsData(null);
        }
    }, [selectedOrg?.id, switchOrgSuccess]);

    React.useEffect(() => {
        if (!groupsData?.records?.length && !canCreateGroup) {
            dispatch(setAppHeaderTitle(t('COMMON:ALL_DEVICES')));
            dispatch(setDrawerHeaderTranslationKey('COMMON:ALL_DEVICES'));
        } else {
            dispatch(setAppHeaderTitle(selectedNode?.name));
            dispatch(setDrawerHeaderTranslationKey('COMMON:ALL_GROUPS'));
        }

        return () => {
            if (!window.location.pathname?.includes(`group/${groupId}`)) dispatch(setAppHeaderTitle(undefined));
        };
    }, [selectedNode, canCreateGroup, groupsData]);

    React.useEffect(() => {
        setSortOrder(sortOption);
    }, [sortOption]);

    React.useEffect(() => {
        const fetchHierarchy = async (currentNode: any, paginationPayload?: any): Promise<void> => {
            try {
                // Pass true as the third argument to force an immediate network reload
                await handleFetchHierarchy(currentNode, paginationPayload, true);
            } catch (error) {
                console.error('Error fetching hierarchy:', error);
            }
        };

        if (sortOrder !== HIERARCHICAL_SORT_ORDER) {
            getFirstLevelGroup({
                body: { page: 0, size: DEFAULT_HIERARCHY_SIZE, sort: { key: 'name', sortType: sortOrder } },
            });
        } else {
            getFirstLevelGroup({ body: { page: 0, size: DEFAULT_HIERARCHY_SIZE } });
        }

        if (controlledExpandedNodes.length > 0) {
            controlledExpandedNodes.forEach((expandedNodeId) => {
                void fetchHierarchy({ id: expandedNodeId }, { page: 0, size: DEFAULT_HIERARCHY_SIZE });
            });
        }
    }, [sortOrder]);

    // Updated this function definition to accept forceRefresh
    const handleFetchHierarchy = async (
        currentNode: any,
        paginationPayload?: any,
        forceRefresh = false
    ): Promise<void> => {
        const nodeId = currentNode?.id;
        // Modified the guard condition: If forceRefresh is TRUE, skip this return completely
        if (shouldSkipFetchHierarchy(forceRefresh, paginationPayload, hierarchyMappingData, nodeId)) {
            return;
        }

        setLoadingHierarchyNode((prevState): any => [
            ...prevState,
            paginationPayload?.page ? `more-button-${nodeId}` : nodeId,
        ]);

        let queryBody = { ...paginationPayload, sort: { key: 'name', sortType: sortOrder } };
        if (sortOrder === HIERARCHICAL_SORT_ORDER) {
            queryBody = { ...paginationPayload };
        }

        const { data: childHierarchyData }: any = await getGroupChildren({
            parent: nodeId,
            body: queryBody,
        });

        if (childHierarchyData?.success) {
            if (nodeId === undefined) {
                setGroupsData((prev: any) => buildGroupsDataUpdate(prev, paginationPayload, childHierarchyData));
            } else {
                setHierarchyMappingData((prev: any) =>
                    buildHierarchyMappingUpdate(prev, currentNode, paginationPayload, childHierarchyData, forceRefresh)
                );
            }
        }

        setLoadingHierarchyNode((prevState): any =>
            prevState.filter(
                (loadingNode) => loadingNode !== (paginationPayload?.page ? `more-button-${nodeId}` : nodeId)
            )
        );
    };

    // const handleCleanHierarchyNode = (parentId: string): void => {
    //     setHierarchyMappingData((prevState: any) => {
    //         const newHierarchyMappingData: any = { ...prevState };
    //         delete newHierarchyMappingData[parentId];
    //         return newHierarchyMappingData;
    //     });
    // };

    const handleCleanHierarchyNode = (parentId: string): void => {
        setHierarchyMappingData((prevState: any) => {
            // Do not delete data to preserve pagination and show more state
            if (!prevState[parentId]) return prevState;

            return {
                ...prevState,
                [parentId]: prevState[parentId],
            };
        });
    };

    const handleNodeClick = (selectedNodeData: any): void => {
        if (selectedNodeData?.type === 'GROUP') {
            navigate(`/${currentRealmName}/group/${selectedNodeData.id}`);
        } else {
            navigate(`/${currentRealmName}/device/${selectedNodeData.id}`);
        }
    };

    const handleOpenAddGroupModal = (group?: any): any => dispatch(addGroupModal({ isOpen: true, group }));

    const hasChildNode = (treeItem: any): boolean =>
        treeItem?.groupCount || treeItem?.deviceCount || hierarchyMappingData[treeItem?.id]?.length;

    const getActions = ({ treeItem }: any): JSX.Element => {
        const item = treeItem ?? {};
        const { alarmCount, type, warningCount } = item;
        if (!alarmCount) return <></>;
        const total = alarmCount + (warningCount ?? 0);
        if (type === 'GROUP') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsActiveOutlined sx={{ color: Colors.gray[500], marginRight: 1 }} />
                    <Typography sx={{ paddingRight: 1, fontWeight: '600', fontSize: 16 }}>{total}</Typography>
                </Box>
            );
        }
        if (type === 'DEVICE' || type === 'GATEWAY') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsActive sx={{ color: Colors.red[500], marginRight: 1 }} />
                    <Typography sx={{ paddingRight: 1, fontWeight: '600', fontSize: 16, color: Colors.red[500] }}>
                        {total}
                    </Typography>
                </Box>
            );
        }
        return <></>;
    };

    const getEmptyStateButton = (): JSX.Element => {
        if (canCreateGroup) {
            return (
                <Button
                    id="add-group"
                    sx={buildEmptyStateButtonSx(theme)}
                    variant={'outlined'}
                    color={'primary'}
                    startIcon={<Add />}
                    onClick={(): void => handleOpenAddGroupModal()}
                >
                    <CustomTransComponent translationKey={'GROUP_MANAGEMENT:BTN.ADD_A_GROUP'} />
                </Button>
            );
        } else if (canCreateDevice) {
            return (
                <Button
                    id="add-group"
                    sx={buildEmptyStateButtonSx(theme)}
                    variant={'contained'}
                    color={'primary'}
                    startIcon={<Add data-testid="AddDeviceIcon" />}
                    onClick={(): void => navigate(`/${currentRealmName}/add-device`)}
                >
                    <CustomTransComponent translationKey={'COMMON:ADD_DEVICE'} />
                </Button>
            );
        }
        return <></>;
    };

    return (
        <Hierarchy
            labelKey={'name'}
            data={groupsData}
            loadMore={handleFetchHierarchy}
            cleanHiearchyNode={handleCleanHierarchyNode}
            hierarchyMappingData={hierarchyMappingData}
            loadingNode={loadingHierarchyNode}
            labelColor={'error'}
            handleTreeNodeSelection={handleNodeClick}
            controlledExpandedNodes={controlledExpandedNodes}
            setControlledExpandedNodes={setControlledExpandedNodes}
            selectionType={'node'} // radio, checkbox, node
            selectedNodes={selectedNode?.id}
            isLoading={!groupsData}
            noDataFoundComponent={
                <Stack className="center w-100">
                    <EmptyState
                        icon={canCreateGroup ? <WorkspacesIcon fontSize={'inherit'} /> : <Device fontSize="inherit" />}
                        title={canCreateGroup ? t('GROUP_MANAGEMENT:LABELS.NO_GROUPS') : t('COMMON:NO_DEVICES')}
                        {...(canCreateGroup && {
                            description: t('COMMON:SELECT_GROUP_NO_DESC'),
                        })}
                        actions={
                            <Stack spacing={2}>
                                {getEmptyStateButton()}
                                <Button
                                    id="hide-panel"
                                    sx={buildEmptyStateButtonSx(theme)}
                                    variant={'outlined'}
                                    color={'primary'}
                                    startIcon={<VisibilityOffIcon />}
                                    onClick={handleHideDrawer}
                                >
                                    <CustomTransComponent translationKey={'GROUP_HIERARCHY:HIDE_THIS_PANEL'} />
                                </Button>
                            </Stack>
                        }
                    />
                </Stack>
            }
            loadingSkeleton={<GroupHierarchySkeleton row={1} />}
            hasChild={hasChildNode}
            getAvatarChild={renderGroupHierarchyIcons}
            pagination={{ records: 'records', total: 'total', size: DEFAULT_HIERARCHY_SIZE }}
            actions={getActions}
        />
    );
};
